import type { HelpRequirements, HelpTip, HelpTipText, HelpVisibilityContext, WhatsNewEntry } from '../interfaces';
import { HELP_MENU_STATE_KEY } from '../constants';
import { ARTICLE_LANGUAGES, HELP_STATE_ACTIONS, HELP_STATE_TYPES, HELP_TIP_STATUS } from '../enums';
import { addDaysToDay } from './day.helper';
import { findHelpStateItem, hasHelpState } from './helpState.helper';

function isHiddenFromEverybody(requires: HelpRequirements | null | undefined) {
  return !!requires?.hidden;
}

function meetsRequirements(requires: HelpRequirements | null | undefined, context: HelpVisibilityContext) {
  if (!requires) return true;
  if (isHiddenFromEverybody(requires)) return false;

  const hasEveryFlag = (requires.feature_flags || []).every((flag) => !!context.shelterFlags[flag]);
  const hasRole = !requires.role || context.can(requires.role);

  return hasEveryFlag && hasRole;
}

// NOTE :: Some features don't exist on a shelter until something happens there (per-species contracts
// need the shelter's contracts migrated). That is not about who may see the feature, so super admins
// don't skip it: New would point at a page that can't be used yet.
function isFeatureAvailable(availableWhen: string[] | null | undefined, context: HelpVisibilityContext) {
  return (availableWhen || []).every((flag) => !!context.shelterFlags[flag]);
}

// NOTE :: A super admin can change who sees a feature from its What's New card. The saved choice
// replaces the one written in the code, for the tip and the article alike. A choice with nothing
// selected is still a choice: it shows the feature to everyone.
function getEffectiveRequires(
  codeRequires: HelpRequirements | null | undefined,
  tipKey: string,
  tipTexts: HelpTipText[]
) {
  const savedRequires = tipTexts.find(({ key }) => key === tipKey)?.requires;

  return savedRequires ?? codeRequires;
}

// NOTE :: A feature is New because it has a tip, not because its article was published. So the
// New window is the tip's (`new_since` to `show_until`), the same for a draft and a published
// article. An article with no tip has no window and is never New.
function getNewSince(entry: WhatsNewEntry) {
  return entry.new_since || entry.published_at || null;
}

// NOTE :: When a super admin turns New off, the window ends the day before it starts. Every place that
// shows New (the feature, the sidebar link, the card, the help button) reads this window, so all of
// them drop New at once, with no rule of their own.
function getEffectiveShowUntil(tip: HelpTip, tipTexts: HelpTipText[]) {
  const tipText = tipTexts.find(({ key }) => key === tip.key);

  if (tipText?.new_disabled) return addDaysToDay(tip.addedOn, -1);

  return tipText?.new_days ? addDaysToDay(tip.addedOn, tipText.new_days) : tip.showUntil;
}

function isWithinNewWindow(entry: WhatsNewEntry, context: HelpVisibilityContext) {
  if (!getNewSince(entry) || !entry.show_until) return false;

  // NOTE :: Compared as shelter calendar days, like the New button in `getHelpTipStatus`. The user's own
  // clock would end the pill a few hours before or after the button in another timezone.
  return context.today <= entry.show_until.slice(0, 10);
}

function isEntryVisible(entry: WhatsNewEntry, context: HelpVisibilityContext) {
  return context.isSuperAdmin || meetsRequirements(entry.requires, context);
}

// NOTE :: An entry stops being New in two ways only: its days run out, or the user hides its tip
// (on the feature or on the What's New card). Reading the article or clicking the sidebar link
// does not clear it.
function isTipHidden(entry: WhatsNewEntry, context: HelpVisibilityContext) {
  const tipKey = entry.tip_key || entry.key;

  return hasHelpState(context.helpState, HELP_STATE_TYPES.ANNOUNCEMENT, tipKey, HELP_STATE_ACTIONS.DISMISSED);
}

function isEntryNew(entry: WhatsNewEntry, context: HelpVisibilityContext) {
  const isAvailable = isFeatureAvailable(entry.available_when, context);

  return (
    isAvailable && isEntryVisible(entry, context) && isWithinNewWindow(entry, context) && !isTipHidden(entry, context)
  );
}

function hasUnseenNews(entries: WhatsNewEntry[], context: HelpVisibilityContext) {
  const seenItem = findHelpStateItem(
    context.helpState,
    HELP_STATE_TYPES.MENU,
    HELP_MENU_STATE_KEY,
    HELP_STATE_ACTIONS.SEEN
  );
  const seenUntil = seenItem?.seen_until ? Date.parse(seenItem.seen_until) : 0;

  return entries.some((entry) => {
    const newSince = getNewSince(entry);
    const isAfterLastSeen = !!newSince && Date.parse(newSince) > seenUntil;

    return isAfterLastSeen && isEntryNew(entry, context);
  });
}

function isNavItemNew(navKey: string, entries: WhatsNewEntry[], context: HelpVisibilityContext) {
  const entry = entries.find(({ nav_key }) => nav_key === navKey);

  return !!entry && isEntryNew(entry, context);
}

function getHelpTipStatus(tip: HelpTip, context: HelpVisibilityContext) {
  const wasDismissed = hasHelpState(
    context.helpState,
    HELP_STATE_TYPES.ANNOUNCEMENT,
    tip.key,
    HELP_STATE_ACTIONS.DISMISSED
  );

  if (wasDismissed) return HELP_TIP_STATUS.HIDDEN;
  if (context.today > tip.showUntil) return HELP_TIP_STATUS.ENDED;

  return HELP_TIP_STATUS.SHOWING;
}

function isHelpTipActive(tip: HelpTip, context: HelpVisibilityContext) {
  const isShowing = getHelpTipStatus(tip, context) === HELP_TIP_STATUS.SHOWING;
  const isAvailable = isFeatureAvailable(tip.availableWhen, context);

  return isShowing && isAvailable && (context.isSuperAdmin || meetsRequirements(tip.requires, context));
}

// NOTE :: `new_since` is UTC on purpose. It is saved as the menu's seen date, and the api reads that
// date as UTC. A date with no zone reads as local time here, so after a save it still looked newer
// than the seen date and the help button never lost its New flag.
function getTipNewWindow(tip: HelpTip) {
  return { new_since: `${tip.addedOn}T00:00:00Z`, show_until: `${tip.showUntil}T23:59:59` };
}

function addTipFields(entry: WhatsNewEntry, tips: HelpTip[]): WhatsNewEntry {
  const tip = tips.find(({ articleKey }) => articleKey === entry.key);

  if (!tip) return entry;

  return {
    ...entry,
    ...getTipNewWindow(tip),
    available_when: tip.availableWhen,
    nav_key: tip.navKey,
    requires: tip.requires,
  };
}

// NOTE :: A tip with no article this user can read still gets a What's New entry, built here from
// the tip. That covers a "Tip only" feature, and a tip whose article is still a draft. It is where
// the user hides the tip or brings it back.
function buildTipEntries(tips: HelpTip[], articleEntries: WhatsNewEntry[]): WhatsNewEntry[] {
  const articleKeys = articleEntries.map(({ key }) => key);

  return tips
    .filter((tip) => !tip.articleKey || !articleKeys.includes(tip.articleKey))
    .map((tip) => ({
      available_when: tip.availableWhen,
      has_article: false,
      key: tip.key,
      nav_key: tip.navKey,
      ...getTipNewWindow(tip),
      published_at: `${tip.addedOn}T00:00:00Z`,
      requires: tip.requires,
      route: tip.route,
      tip_key: tip.key,
      title: '',
    }));
}

// NOTE :: Newest first by the day the feature became New (its tip), then by publish date, so the
// order holds inside one month too. A draft with no tip has no date and sorts first for the super
// admins who see it.
function getEntrySortTime(entry: WhatsNewEntry) {
  const date = getNewSince(entry);

  return date ? Date.parse(date) : Number.MAX_SAFE_INTEGER;
}

function buildWhatsNewEntries(articleEntries: WhatsNewEntry[], tips: HelpTip[], context: HelpVisibilityContext) {
  const datedArticleEntries = articleEntries.map((entry) => addTipFields(entry, tips));

  return [...datedArticleEntries, ...buildTipEntries(tips, articleEntries)]
    .filter((entry) => isEntryVisible(entry, context))
    .sort((first, second) => getEntrySortTime(second) - getEntrySortTime(first));
}

function getNewestPublishedAt(entries: WhatsNewEntry[]) {
  const publishedDates = entries.map(getNewSince).filter((date): date is string => !!date);

  return publishedDates.sort((first, second) => Date.parse(second) - Date.parse(first))[0] || null;
}

function resolveArticleLanguage(locale: string | null | undefined) {
  const language = (locale || '').toLowerCase();

  if (language.startsWith('pt')) return ARTICLE_LANGUAGES.PT_BR;
  if (language.startsWith('es')) return ARTICLE_LANGUAGES.ES;

  return ARTICLE_LANGUAGES.EN;
}

export {
  buildWhatsNewEntries,
  getEffectiveRequires,
  getEffectiveShowUntil,
  getHelpTipStatus,
  getNewestPublishedAt,
  hasUnseenNews,
  isEntryNew,
  isEntryVisible,
  isFeatureAvailable,
  isHelpTipActive,
  isHiddenFromEverybody,
  isNavItemNew,
  meetsRequirements,
  resolveArticleLanguage,
};
